import { getColumnTemplateById, getColumnTemplatesQuery, getDefaultColumnTemplate } from './http/columnTemplate';
import type { QueryObserverOptions } from '@tanstack/react-query';
import type {
    ColumnTemplate,
    ConcreteColumnTemplate,
    KnownColumnTemplateKey,
} from '@thinkalpha/ui-types/contracts/column-template';
import type { ResourceQuery } from '@thinkalpha/ui-types/contracts/resource-query';
import type { LibraryQueryResult } from 'src/models/LibraryModel';

// Constants
export const COLUMN_TEMPLATES = 'column-templates';
export const LIBRARY = 'library';

/**
 * Generates a query to get a ConcreteColumnTemplate by id
 *
 * @param id a string ConcreteColumnTemplate id, or null
 * @returns an object with queryKey and queryFn
 */
export function getColumnTemplateByIdQuery(id: string): QueryObserverOptions<ConcreteColumnTemplate> {
    return {
        queryKey: [COLUMN_TEMPLATES, id],
        queryFn: () => getColumnTemplateById(id),
    };
}

/**
 * Generates a query to get a default ConcreteColumnTemplate by key
 *
 * @param id a KnownColumnTemplateKey
 * @returns an object with queryKey and queryFn
 */
export function getDefaultColumnTemplateQuery(
    key: KnownColumnTemplateKey,
): QueryObserverOptions<ConcreteColumnTemplate> {
    return {
        queryKey: [COLUMN_TEMPLATES, 'default', key],
        queryFn: () => getDefaultColumnTemplate(key),
    };
}

export function getLiteralColumnTemplateQuery(columnTemplate: ColumnTemplate): QueryObserverOptions<ColumnTemplate> {
    return {
        queryKey: [COLUMN_TEMPLATES, 'literal', columnTemplate],
        queryFn: () => columnTemplate,
        gcTime: 0,
    };
}

export function getColumnTemplateRecordsForLibraryQuery(
    resourceQuery: ResourceQuery,
    enabled = true,
): QueryObserverOptions<LibraryQueryResult<ConcreteColumnTemplate>> {
    return {
        queryKey: [COLUMN_TEMPLATES, LIBRARY, resourceQuery],
        queryFn: async () => {
            const result = await getColumnTemplatesQuery(resourceQuery);

            return {
                total: result.count,
                rows: result.results,
            };
        },
        enabled,
    };
}
